import { useCallback } from 'react'

import { PureQueryOptions } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { SaveProductSettingsListRecordDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

// Three op kinds. The discriminated union forces the consumer to
// supply exactly the right fields per kind, no more.
export type ListOp =
  | { op: 'create'; valueJson: string }
  | { op: 'update'; id: string; valueJson: string }
  | { op: 'delete'; id: string }

// Per-op outcome reported by the backend after applying the batch.
// One result per input op, in the same order. `success` is true only
// when the op was applied (and the whole transaction committed) — on
// any failure within the batch, every op's `success` is false because
// the transaction rolled back.
export type ListOpResult = {
  index: number
  success: boolean
  id: string | null
  valueJson: string | null
  errors: string[]
}

export type SaveOpsResult =
  | { success: true; opResults: ListOpResult[] }
  | { success: false; opResults: ListOpResult[]; mutationErrors: string[] }

interface Params {
  productId: string
  listName: string
  companyId: string
  // After a successful save, refetch the consumer's own read queries so
  // their list stays in sync. Reads don't go through the framework — the
  // framework can't refresh them without being told what to refresh.
  refetchQueries?: Array<DocumentNode | PureQueryOptions | string>
  awaitRefetchQueries?: boolean
}

export const useProductSettingsListMutations = ({
  productId,
  listName,
  companyId,
  refetchQueries,
  awaitRefetchQueries,
}: Params) => {
  const [save, { loading }] = useMutation(SaveProductSettingsListRecordDocument, {
    extractorFn: (data) => data.productSettingsMutations.saveProductsettingsListRecord,
    refetchQueries,
    awaitRefetchQueries,
  })

  const saveOps = useCallback(
    async (ops: ListOp[]): Promise<SaveOpsResult> => {
      const result = await save({
        variables: {
          productId,
          companyId,
          listName,
          ops: ops.map((op) => {
            switch (op.op) {
              case 'create':
                return { op: 'create', id: null, valueJson: op.valueJson }
              case 'update':
                return { op: 'update', id: op.id, valueJson: op.valueJson }
              case 'delete':
                return { op: 'delete', id: op.id, valueJson: null }
            }
          }),
        },
      })

      const handled = handleResult(result, () => {})

      if (!handled.success) {
        return {
          success: false,
          opResults: [],
          mutationErrors: handled.errors.map((e) => {
            if (e.__typename === 'StructuredError') {
              return `${e.field}: ${e.messages.join(', ')}`
            }
            if (e.__typename === 'SimpleError') {
              return e.message
            }
            return 'Unknown error'
          }),
        }
      }

      const json = handled.data.listRecord?.opResultsJson ?? '[]'
      let opResults: ListOpResult[]
      try {
        opResults = JSON.parse(json)
      } catch {
        return {
          success: false,
          opResults: [],
          mutationErrors: ['Malformed server response: could not parse op results'],
        }
      }

      // Whole-batch outcome: every op succeeded → batch committed.
      // Any per-op failure means the transaction rolled back; surface
      // that to the consumer.
      const allSucceeded = opResults.every((r) => r.success)

      return allSucceeded
        ? { success: true, opResults }
        : { success: false, opResults, mutationErrors: [] }
    },
    [save, productId, companyId, listName]
  )

  return { saveOps, loading }
}
